import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  getLibraryQuestions,
  createLibraryQuestion,
  updateLibraryQuestion,
  deleteLibraryQuestion,
  getTenants,
  pushQuestionsToTenant,
} from "../apiHelper";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import ResponsiveTable from "../components/ResponsiveTable";
import {
  BookOpen,
  Plus,
  Edit,
  Trash2,
  Search,
  Send,
  Filter,
  X,
} from "lucide-react";
import QuestionLibraryForm from "../components/questionLibrary/QuestionLibraryForm";
import PushToTenantDialog from "../components/questionLibrary/PushToTenantDialog";

const QuestionLibrary = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showPushDialog, setShowPushDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [tenants, setTenants] = useState([]);
  const { getAccessTokenSilently } = useAuth();

  useEffect(() => {
    fetchQuestions();
    fetchTenants();
  }, [searchKeyword, categoryFilter, activeFilter]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const params = {
        page: 1,
        limit: 100,
      };

      if (searchKeyword) {
        params.search = searchKeyword;
      }
      if (categoryFilter) {
        params.category = categoryFilter;
      }
      if (activeFilter !== "") {
        params.active = activeFilter === "true";
      }

      const result = await getLibraryQuestions(token, params);

      if (result.success) {
        setQuestions(result.data || []);
      } else {
        throw new Error(result.message || "Failed to load questions");
      }
    } catch (err) {
      setError(err.message || "Failed to load questions");
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const token = await getAccessTokenSilently();
      const result = await getTenants(token, {});
      if (result.success || result.data) {
        setTenants(result.data || result || []);
      }
    } catch (err) {
      console.error("Failed to load tenants:", err);
    }
  };

  const handleAddQuestion = () => {
    setEditingQuestion(null);
    setShowForm(true);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setShowForm(true);
  };

  const handleSaveQuestion = async (questionData) => {
    try {
      setMessage(null);
      const token = await getAccessTokenSilently();

      if (editingQuestion) {
        const result = await updateLibraryQuestion(
          token,
          editingQuestion._id,
          questionData
        );
        if (result.success) {
          setMessage({ type: "success", text: "Question updated successfully" });
          setShowForm(false);
          setEditingQuestion(null);
          fetchQuestions();
        } else {
          throw new Error(result.message || "Failed to update question");
        }
      } else {
        const result = await createLibraryQuestion(token, questionData);
        if (result.success) {
          setMessage({ type: "success", text: "Question created successfully" });
          setShowForm(false);
          fetchQuestions();
        } else {
          throw new Error(result.message || "Failed to create question");
        }
      }
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Failed to save question" });
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm("Are you sure you want to delete this question?")) {
      return;
    }

    try {
      setMessage(null);
      const token = await getAccessTokenSilently();
      const result = await deleteLibraryQuestion(token, questionId);

      if (result.success) {
        setMessage({ type: "success", text: "Question deleted successfully" });
        fetchQuestions();
      } else {
        throw new Error(result.message || "Failed to delete question");
      }
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Failed to delete question" });
    }
  };

  const handlePushToTenant = (questionIds) => {
    setSelectedQuestions(questionIds);
    setShowPushDialog(true);
  };

  const handlePushConfirm = async (tenantId, active) => {
    try {
      setMessage(null);
      const token = await getAccessTokenSilently();
      const result = await pushQuestionsToTenant(token, tenantId, {
        questionIds: selectedQuestions,
        active,
      });

      if (result.success) {
        setMessage({
          type: "success",
          text: `Successfully pushed ${result.data.pushed} question(s) to tenant`,
        });
        setShowPushDialog(false);
        setSelectedQuestions([]);
      } else {
        throw new Error(result.message || "Failed to push questions");
      }
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Failed to push questions" });
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      wellbeing: "bg-teal-100 text-teal-800 border-teal-200",
      stress: "bg-red-100 text-red-800 border-red-200",
      engagement: "bg-blue-100 text-blue-800 border-blue-200",
      culture: "bg-purple-100 text-purple-800 border-purple-200",
      workload: "bg-orange-100 text-orange-800 border-orange-200",
      custom: "bg-gray-100 text-gray-800 border-gray-200",
    };
    return colors[category] || colors.custom;
  };

  const columns = [
    {
      key: "question",
      label: "Question",
      mobileLabel: "Question",
      render: (question) => (
        <div className="font-medium !text-gray-900">{question.question}</div>
      ),
    },
    {
      key: "type",
      label: "Type",
      mobileLabel: "Type",
      render: (question) => (
        <Badge variant="secondary" className="!bg-gray-100 !text-gray-800 border border-gray-200">
          {question.type}
        </Badge>
      ),
    },
    {
      key: "category",
      label: "Category",
      mobileLabel: "Category",
      render: (question) => (
        <Badge className={`${getCategoryColor(question.category)} border`}>
          {question.category}
        </Badge>
      ),
    },
    {
      key: "tags",
      label: "Tags",
      mobileLabel: "Tags",
      render: (question) => (
        <div className="flex flex-wrap gap-1">
          {question.tags && question.tags.length > 0 ? (
            question.tags.slice(0, 3).map((tag, idx) => (
              <Badge key={idx} variant="outline" className="!text-gray-700 !border-gray-300 text-xs">
                {tag}
              </Badge>
            ))
          ) : (
            <span className="text-gray-500 text-sm">No tags</span>
          )}
        </div>
      ),
    },
    {
      key: "active",
      label: "Status",
      mobileLabel: "Status",
      render: (question) => (
        <Badge
          variant={question.active ? "default" : "secondary"}
          className={question.active 
            ? "!bg-green-100 !text-green-800 !border-green-200 border" 
            : "!bg-gray-100 !text-gray-800 !border-gray-200 border"}
        >
          {question.active ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      mobileLabel: "Actions",
      render: (question) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEditQuestion(question)}
            className="h-9 w-9 p-0 hover:bg-blue-50"
            title="Edit question"
          >
            <Edit className="h-5 w-5 text-blue-600" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteQuestion(question._id)}
            className="h-9 w-9 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
            title="Delete question"
          >
            <Trash2 className="h-5 w-5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handlePushToTenant([question._id])}
            className="h-9 w-9 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            title="Push to tenant"
          >
            <Send className="h-5 w-5" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 p-3 lg:p-6">
      <div className="max-w-7xl mx-auto space-y-6 lg:space-y-8 w-full overflow-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
              Question Library
            </h1>
            <p className="text-sm lg:text-base text-gray-600">
              Manage question templates and push them to tenant databases
            </p>
          </div>
          <Button 
            onClick={handleAddQuestion} 
            className="!bg-blue-600 hover:!bg-blue-700 !text-white font-semibold px-6 py-3 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Question
          </Button>
        </div>

        {/* Messages */}
        {message && (
          <div
            className={`p-4 rounded-lg ${
              message.type === "success"
                ? "bg-green-50 text-green-800 border border-green-200"
                : "bg-red-50 text-red-800 border border-red-200"
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Filters */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl shadow-gray-200/50 rounded-2xl">
          <CardContent className="p-4 lg:p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search questions..."
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Categories</option>
                <option value="wellbeing">Wellbeing</option>
                <option value="stress">Stress</option>
                <option value="engagement">Engagement</option>
                <option value="culture">Culture</option>
                <option value="workload">Workload</option>
                <option value="custom">Custom</option>
              </select>
              <select
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
              {(categoryFilter || activeFilter || searchKeyword) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setCategoryFilter("");
                    setActiveFilter("");
                    setSearchKeyword("");
                  }}
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Questions Table */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl shadow-gray-200/50 rounded-2xl">
          <CardHeader className="pb-4 lg:pb-6">
            <CardTitle className="flex items-center gap-2 lg:gap-3 text-lg lg:text-xl">
              <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                <BookOpen className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
              </div>
              <span className="text-base lg:text-xl text-gray-900 font-semibold">Question Library</span>
              {questions && (
                <Badge
                  variant="outline"
                  className="ml-2 lg:ml-3 bg-blue-50 text-blue-700 border-blue-200 px-2 py-1 lg:px-3 lg:py-1 rounded-lg text-xs lg:text-sm"
                >
                  {questions.length} questions
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ResponsiveTable
              columns={columns}
              data={questions}
              loading={loading}
              emptyMessage="No questions found. Create your first question to get started."
            />
          </CardContent>
        </Card>

        {/* Form Modal */}
        {showForm && (
          <QuestionLibraryForm
            question={editingQuestion}
            onSave={handleSaveQuestion}
            onCancel={() => {
              setShowForm(false);
              setEditingQuestion(null);
            }}
          />
        )}

        {/* Push Dialog */}
        {showPushDialog && (
          <PushToTenantDialog
            tenants={tenants}
            questionCount={selectedQuestions.length}
            onPush={handlePushConfirm}
            onCancel={() => {
              setShowPushDialog(false);
              setSelectedQuestions([]);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default QuestionLibrary;

